import { Component } from '@theme/component';
import { CartErrorEvent, CartLinesUpdateEvent, StandardEvents } from '@shopify/events';
import { CartItemsComponent } from '@theme/component-cart-items';
import { DrawerCloseEvent, DrawerOpenEvent } from '@theme/theme-drawer';
import { ThemeEvents } from '@theme/events';
import { debounce, fetchConfig, prefersReducedMotion } from '@theme/utilities';

/*
 * Qallo cart: beloningsbalk met gratis cadeau, upsells en verzendbescherming in de cart drawer.
 * Vervangt de Upcart-app. Instellingen: Thema-instellingen > Qallo winkelmandje.
 * Wijzigingen aan het winkelmandje gaan via dezelfde events als Horizon (CartLinesUpdateEvent met
 * section-HTML), zodat de drawer, het teller-icoon en analytics op dezelfde manier bijwerken.
 */

const GIFT_PROPERTY = '_qallo_gift';
/** Cadeaulijnen die Upcart nog toevoegde; tellen als cadeau zodat er geen tweede fles bijkomt. */
const LEGACY_GIFT_PROPERTY = '__upcartRewardProduct';
/**
 * Lijnattribuut van een LoyaltyLion-beloning ("Seamless" gratis product, waarde = id van de redemption). Zo een lijn
 * telt nooit mee voor de cadeauniveaus, net als in de function van de app Qallo Cart Gift.
 * Zelfde sleutel in snippets/qallo-cart-rewards.liquid en qallo-cart-config.liquid.
 */
const LION_REWARD_PROPERTY = '__lion_sfp_id';
/**
 * Property van de verzendbeschermingslijn. Deze module voegt ze toe met die property; theme en function herkennen de
 * lijn eraan (de theme als vangnet ook aan het product, voor lijnen van voor deze property).
 * Zelfde sleutel in snippets/qallo-cart-rewards.liquid, qallo-cart-config.liquid, qallo-cart-protection.liquid en
 * cart-products.liquid.
 */
const PROTECTION_PROPERTY = '_qallo_protection';
/**
 * Property van een lijn die via de upsells in het winkelmandje is toegevoegd (waarde "1"). Enkel markering: de lijn
 * is een gewone betaalde lijn en telt overal mee (cadeauniveaus, gratis verzending, upsell-basis). Komt mee tot in de
 * bestelling (line item properties), zodat Shopify admin, exports en het datawarehouse zien welke stuks via een
 * upsell kwamen. De drawer toont ze niet (snippets/cart-products.liquid verbergt properties die met "_" beginnen).
 * Zie README.md > Upsell-property voor de Shopify Flow die er een ordertag van maakt.
 */
const UPSELL_PROPERTY = '_qallo_upsell';
const UPSELLS_SECTION_ID = 'qallo-cart-upsells';
const RECOMMENDATIONS_LIMIT = 10;
const MAX_GIFT_ROUNDS = 4;

/**
 * @typedef {object} CartLine
 * @property {string} key
 * @property {number} variantId
 * @property {number} productId
 * @property {number} quantity
 * @property {number} linePrice - final_line_price in centen (na kortingen op productniveau)
 * @property {number} [orderDiscount] - Toegewezen deel van kortingen op orderniveau (kortingscode), in centen
 * @property {boolean} giftProperty - Lijn met property _qallo_gift (of de oude Upcart-property)
 * @property {boolean} [lionReward] - LoyaltyLion-beloning (property __lion_sfp_id)
 * @property {boolean} [protectionProperty] - Verzendbescherming (property _qallo_protection)
 * @property {boolean} [bottleCodeProperty] - Losse fles die de theme toevoegde voor een fles-code (property _qallo_bottle_code)
 */

/**
 * @typedef {object} GiftTier
 * @property {number} threshold - in centen (winkelmetaveld qallo.cart_gift_threshold)
 * @property {number} productId - het cadeauproduct (winkelmetaveld qallo.cart_gift_product)
 * @property {number} variantId
 * @property {boolean} available
 */

/**
 * @typedef {object} Tier
 * @property {number} tier - 1 tot 3
 * @property {'info' | 'gift'} type
 * @property {number} threshold - in centen, voor het land van de bezoeker (snippets/qallo-cart-tier-threshold.liquid)
 */

/**
 * @typedef {object} QalloCartConfig
 * @property {GiftTier[]} giftTiers - Leeg als er geen cadeauproduct is of niets beschikbaar: dan nooit een cadeau
 * @property {Tier[]} tiers - Zichtbare niveaus met de drempel voor het land van de bezoeker
 * @property {number} protectionProductId - 0 als de verzendbescherming uit staat (vangnet naast de property)
 * @property {BottleCodeConfig} bottleCode
 * @property {CartLine[]} lines
 */

/**
 * @typedef {object} BottleCodeConfig
 * @property {string[]} codes - Codes met de tag free-bottle-code (winkelmetaveld qallo.free_bottle_codes, Shopify Flow)
 * @property {Record<string, number>} pairs - Variant zonder fles -> variant met fles
 * @property {number} bottleProductId - De losse fles (0 = geen)
 * @property {number} bottleVariantId
 * @property {boolean} available
 */

/**
 * @typedef {{ type: 'add', variantId: number } | { type: 'remove', key: string } | { type: 'update', key: string, quantity: number } | { type: 'addBottle', variantId: number, code: string, token: string } | { type: 'swapBottle', key: string, quantity: number, variantId: number, code: string, token: string }} GiftAction
 */

/** Events die deze module zelf verstuurt voor het cadeau; de cadeaulogica reageert er niet op (geen lussen). */
const ownGiftEvents = new WeakSet();

class CartRequestError extends Error {
  /**
   * @param {string} message - Technische melding (console)
   * @param {number} status - HTTP-status
   * @param {string} [userMessage] - Melding van Shopify voor de klant (bv. "niet meer op voorraad"), indien aanwezig
   */
  constructor(message, status, userMessage = '') {
    super(message);
    this.name = 'CartRequestError';
    this.status = status;
    this.userMessage = userMessage;
  }

  /** Shopify weigert de wijziging zelf (bv. uitverkocht): opnieuw proberen heeft geen zin. */
  get isRejection() {
    return this.status === 400 || this.status === 404 || this.status === 422;
  }
}

/**
 * @param {unknown} error
 * @param {string} fallback - Algemene foutmelding in de taal van de winkel
 * @returns {string}
 */
function userErrorMessage(error, fallback) {
  return error instanceof CartRequestError && error.userMessage ? error.userMessage : fallback;
}

/**
 * Herkent een volledig winkelmandje zoals /cart.js of /cart/change.js het teruggeeft.
 * @param {any} data
 */
function isFullCart(data) {
  return Boolean(data && Array.isArray(data.items) && typeof data.item_count === 'number' && 'token' in data);
}

/** @returns {string[]} De section-ID's van alle cart-items-componenten op de pagina (drawer of winkelwagenpagina). */
function getCartSectionIds() {
  const ids = new Set();
  for (const element of document.querySelectorAll('cart-items-component')) {
    if (element instanceof HTMLElement && element.dataset.sectionId) ids.add(element.dataset.sectionId);
  }
  return [...ids];
}

/** Wachttijden voor een nieuwe poging na 429 (Shopify beperkt cart-requests) of een serverfout: 1 s, 2 s, 4 s. */
const RETRY_DELAYS_MS = [1000, 2000, 4000];

/** @param {number} ms */
const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

/**
 * @param {AbortSignal} [signal]
 * @returns {Promise<any>} Het winkelmandje uit /cart.js
 */
async function fetchCart(signal) {
  // Na een geslaagde wijziging is het verse winkelmandje nodig om de drawer bij te werken:
  // bij 429 of een serverfout opnieuw proberen na 1 s, 2 s en 4 s
  for (let attempt = 0; ; attempt++) {
    const response = await fetch(`${Theme.routes.cart_url}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store',
      signal,
    });
    if (response.ok) return response.json();

    const retryable = response.status === 429 || response.status >= 500;
    if (!retryable || attempt >= RETRY_DELAYS_MS.length) {
      throw new CartRequestError(`Cart fetch failed: ${response.status}`, response.status);
    }
    await wait(RETRY_DELAYS_MS[attempt] ?? 4000);
  }
}

/**
 * POST naar een cart-endpoint, met de section-ID's van het winkelmandje zodat het antwoord verse section-HTML bevat.
 * @param {string} url
 * @param {Record<string, unknown>} body
 * @returns {Promise<any>}
 */
async function postCart(url, body) {
  const sectionIds = getCartSectionIds();
  const payload = { ...body };
  if (sectionIds.length > 0) {
    payload.sections = sectionIds.join(',');
    payload.sections_url = window.location.pathname;
  }

  // Eigen request: de bewaking van externe cart-requests (CartRequestWatcher) negeert het
  const release = cartRequestWatcher.claim();
  /** @type {Response} */
  let response;
  /** @type {any} */
  let data;
  try {
    // 429: Shopify heeft de wijziging niet uitgevoerd, opnieuw versturen is veilig (na 1 s, 2 s, 4 s)
    for (let attempt = 0; ; attempt++) {
      response = await fetch(url, fetchConfig('json', { body: JSON.stringify(payload) }));
      if (response.status !== 429 || attempt >= RETRY_DELAYS_MS.length) break;
      await wait(RETRY_DELAYS_MS[attempt] ?? 4000);
    }
    data = await response.json().catch(() => ({}));
  } finally {
    release();
  }

  if (!response.ok || data.errors || (typeof data.status === 'number' && data.status >= 400)) {
    const status = response.ok ? Number(data.status) || 422 : response.status;
    const userMessage =
      status === 429 ? '' : data.description || data.message || (typeof data.errors === 'string' ? data.errors : '');
    throw new CartRequestError(`Cart request failed: ${status}`, status, userMessage);
  }

  return data;
}

/**
 * Meldt een geslaagde wijziging met een CartLinesUpdateEvent, met hetzelfde resultaat als Horizon (product-form,
 * cart-items): de drawer morpht met de meegegeven section-HTML, teller en analytics werken bij.
 * Enkel na een geslaagd request; bij een fout volgt alleen een CartErrorEvent.
 * @param {EventTarget} target
 * @param {{ action: 'add', lines: { merchandiseId: string, quantity: number }[] } | { action: 'update' | 'remove', lines: { id: string, quantity: number }[] }} change
 * @param {any} cart - volledig winkelmandje (/cart.js of /cart/change.js)
 * @param {Record<string, string> | undefined} sections
 * @param {string} source
 * @param {(event: Event) => void} [beforeDispatch]
 */
function dispatchCartUpdate(target, change, cart, sections, source, beforeDispatch) {
  const deferred = CartLinesUpdateEvent.createPromise();
  const event = new CartLinesUpdateEvent({ ...change, context: 'cart', promise: deferred.promise });
  beforeDispatch?.(event);
  target.dispatchEvent(event);
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: {
      items: cart.items,
      itemCount: cart.item_count,
      sections,
      source,
      didError: false,
    },
  });
}

/**
 * @param {EventTarget} target
 * @param {unknown} error
 * @param {string} fallback
 */
function dispatchCartError(target, error, fallback) {
  target.dispatchEvent(
    new CartErrorEvent({
      error: error instanceof Error && error.message ? error.message : fallback,
      code: error instanceof CartRequestError && error.isRejection ? 'INVALID' : 'SERVICE_UNAVAILABLE',
    })
  );
}

/**
 * Deel van kortingen op orderniveau (kortingscode zoals QIAN, cart_level_discount_applications) dat Shopify aan
 * deze lijn toewijst. In /cart.js zit dat in total_discount (alle kortingen) en niet in line_level_total_discount;
 * final_line_price bevat het niet, line_price wel.
 * @param {any} item - item uit /cart.js
 */
function orderDiscountOf(item) {
  const total = Number(item.total_discount);
  const lineLevel = Number(item.line_level_total_discount);
  if (Number.isFinite(total) && Number.isFinite(lineLevel)) return Math.max(0, total - lineLevel);
  const finalLinePrice = Number(item.final_line_price);
  const linePrice = Number(item.line_price);
  if (Number.isFinite(finalLinePrice) && Number.isFinite(linePrice)) return Math.max(0, finalLinePrice - linePrice);
  return 0;
}

/**
 * @param {any[]} items - items uit /cart.js
 * @returns {CartLine[]}
 */
function normalizeLines(items) {
  return (items ?? []).map((item) => ({
    key: item.key,
    variantId: Number(item.variant_id),
    productId: Number(item.product_id),
    quantity: Number(item.quantity),
    linePrice: Number(item.final_line_price),
    orderDiscount: orderDiscountOf(item),
    giftProperty: Boolean(item.properties?.[GIFT_PROPERTY] || item.properties?.[LEGACY_GIFT_PROPERTY]),
    lionReward: Boolean(String(item.properties?.[LION_REWARD_PROPERTY] ?? '').trim()),
    protectionProperty: Boolean(item.properties?.[PROTECTION_PROPERTY]),
    bottleCodeProperty: Boolean(item.properties?.[BOTTLE_CODE_PROPERTY]),
  }));
}

/**
 * Cadeaulijn: enkel een lijn met de cadeau-property (ook de oude Upcart-property). Een lijn van het cadeauproduct
 * zonder property is een fles die de klant zelf kocht: een gewone betaalde lijn die nooit verwijderd of verminderd
 * wordt. Dezelfde regel staat in snippets/cart-products.liquid en snippets/qallo-cart-rewards.liquid.
 * @param {QalloCartConfig} _config
 * @param {CartLine} line
 */
function isGiftLine(_config, line) {
  return line.giftProperty;
}

/**
 * Verzendbeschermingslijn: property _qallo_protection (zoals de function van Qallo Cart Gift), als vangnet het
 * product (lijnen toegevoegd voor die property bestond).
 * @param {QalloCartConfig} config
 * @param {CartLine} line
 */
function isProtectionLine(config, line) {
  if (line.protectionProperty) return true;
  return config.protectionProductId > 0 && line.productId === config.protectionProductId;
}

/**
 * Unieke product-ID's van het winkelmandje zonder cadeau en verzendbescherming, numeriek gesorteerd
 * (uit de items van een cart-event of data-cart-product-ids in snippets/qallo-cart-upsells.liquid).
 * @param {Array<number | string>} productIds
 */
function cartSignature(productIds) {
  return [...new Set(productIds.map(Number))].sort((a, b) => a - b).join(',');
}

/** @returns {QalloCartConfig | null} */
function readConfig() {
  const script = document.querySelector('script[data-qallo-cart-config]');
  if (!script?.textContent) return null;

  try {
    const config = JSON.parse(script.textContent);
    return {
      giftTiers: Array.isArray(config.giftTiers) ? config.giftTiers : [],
      protectionProductId: Number(config.protectionProductId) || 0,
      bottleCode: {
        codes: Array.isArray(config.bottleCode?.codes) ? config.bottleCode.codes.map((/** @type {unknown} */ code) => String(code).trim().toUpperCase()) : [],
        pairs: config.bottleCode?.pairs && typeof config.bottleCode.pairs === 'object' ? config.bottleCode.pairs : {},
        bottleProductId: Number(config.bottleCode?.bottleProductId) || 0,
        bottleVariantId: Number(config.bottleCode?.bottleVariantId) || 0,
        available: Boolean(config.bottleCode?.available),
      },
      tiers: Array.isArray(config.tiers) ? config.tiers : [],
      lines: Array.isArray(config.lines) ? config.lines : [],
    };
  } catch (error) {
    console.warn('[qallo-cart] Invalid config', error);
    return null;
  }
}

/**
 * Winkelmandwaarde voor een niveau, na alle kortingen: op productniveau (final_line_price) én op orderniveau
 * (kortingscode, het deel dat Shopify aan de lijn toewijst), dus wat de klant betaalt. Nooit mee, enkel op
 * lijneigenschappen: cadeaulijnen (_qallo_gift of de oude Upcart-property __upcartRewardProduct, ook de korting die
 * Shopify eraan toewijst) en verzendbescherming (_qallo_protection, als vangnet het product). Gekochte drinkflessen
 * tellen mee.
 * - 'info' (gratis verzending): Shopify rekent de voorwaarde van een verzendtarief op het subtotaal na kortingen.
 * - 'gift': daarnaast ook niet mee: LoyaltyLion-beloningen (__lion_sfp_id). Dezelfde vier properties sluit de
 *   function van de app Qallo Cart Gift uit; geen tags. De theme beslist op het bedrag na kortingen; de
 *   function controleert het bedrag vóór kortingen als ondergrens. Vóór kortingen is altijd minstens zoveel: voegt
 *   de theme het cadeau toe, dan maakt de function het ook gratis. Zakt een kortingscode in de drawer het bedrag
 *   onder de drempel, dan haalt de theme het cadeau weg. Een code die de klant pas in de checkout invult, ziet de
 *   theme niet: het cadeau blijft staan en blijft gratis (de function kijkt vóór kortingen).
 * Dezelfde regels staan in snippets/qallo-cart-rewards.liquid (die rendert de balk) en qallo-cart-config.liquid.
 * @param {QalloCartConfig} config
 * @param {CartLine[]} lines
 * @param {'info' | 'gift'} type
 */
function tierValue(config, lines, type) {
  let total = 0;
  for (const line of lines) {
    if (isGiftLine(config, line) || isProtectionLine(config, line)) continue;
    if (type === 'gift' && line.lionReward) continue;
    total += line.linePrice - (line.orderDiscount ?? 0);
  }
  return Math.max(0, total);
}

/**
 * Waarde voor de cadeauniveaus (zie tierValue).
 * @param {QalloCartConfig} config
 * @param {CartLine[]} lines
 */
function eligibleValue(config, lines) {
  return tierValue(config, lines, 'gift');
}

/**
 * Bepaalt de volgende stap om het winkelmandje in lijn te brengen met de regels (één stap per keer):
 * - per bereikt cadeauniveau precies één cadeaulijn (met property) met aantal 1, naast eventueel gekochte flessen
 *   (aparte lijnen zonder property, die blijven altijd ongemoeid);
 * - geen cadeaulijnen onder de drempel, en geen cadeaulijnen van een ander product dan het huidige cadeauproduct
 *   (winkelmetaveld; na een wissel van het cadeau van de maand of oude Upcart-cadeaus): die gaan weg, daarna komt
 *   het huidige cadeau erbij als de drempel gehaald is. Zonder cadeauproduct (giftTiers leeg) gaat elke cadeaulijn weg;
 * - geen verzendbescherming meer als er naast cadeau en verzendbescherming geen product meer is.
 * @param {QalloCartConfig} config
 * @param {CartLine[]} lines
 * @returns {GiftAction | null}
 */
function decideGiftAction(config, lines) {
  const value = eligibleValue(config, lines);
  /** @type {Map<number, GiftTier>} Bereikte cadeauniveaus per cadeauproduct */
  const reached = new Map();
  for (const tier of config.giftTiers) {
    if (tier.variantId && value >= tier.threshold) reached.set(Number(tier.productId), tier);
  }

  const giftLines = lines.filter((line) => isGiftLine(config, line));

  // Niveau niet (meer) bereikt, of cadeaulijn van een ander product dan het huidige cadeauproduct: weg
  for (const line of giftLines) {
    if (!reached.has(line.productId)) return { type: 'remove', key: line.key };
  }

  for (const [productId, tier] of reached) {
    const productLines = giftLines.filter((line) => line.productId === productId);
    if (productLines.length === 0) {
      if (tier.available) return { type: 'add', variantId: Number(tier.variantId) };
      continue;
    }

    // Eén cadeaulijn houden, de rest weg, aantal 1
    const keep = productLines[0];
    const extra = productLines.find((line) => line !== keep);
    if (extra) return { type: 'remove', key: extra.key };
    if (keep && keep.quantity > 1) return { type: 'update', key: keep.key, quantity: 1 };
  }

  // Enkel nog verzendbescherming (en eventueel het cadeau, dat hierboven al weg is): verzendbescherming weg
  const hasProducts = lines.some((line) => !isGiftLine(config, line) && !isProtectionLine(config, line));
  if (!hasProducts) {
    const protection = lines.find((line) => isProtectionLine(config, line));
    if (protection) return { type: 'remove', key: protection.key };
  }

  return null;
}

/**
 * Property op de losse fles die de fles-code toevoegde (waarde = de code). Enkel markering voor bestelling en
 * datawarehouse; de drawer toont properties met "_" niet. Een omgezette Starter-variant krijgt geen property
 * (dat gebeurt in één /cart/update.js, dat geen properties kent).
 */
const BOTTLE_CODE_PROPERTY = '_qallo_bottle_code';
/** Winkelmandje-attribuut na het omzetten van een Starter-variant naar de variant met fles (waarde = de code). */
const BOTTLE_SWAP_ATTRIBUTE = '_qallo_bottle_swap';
const BOTTLE_CODE_MEMORY_PREFIX = 'qallo-bottle-code:';

/**
 * Fles-code al eens afgehandeld voor dit winkelmandje? Haalt de klant de toegevoegde fles zelf weg, dan voegt de
 * theme ze niet opnieuw toe (sessionStorage, per winkelmandje en code).
 * @param {string} token
 * @param {string} code
 */
function bottleCodeHandled(token, code) {
  try {
    return sessionStorage.getItem(`${BOTTLE_CODE_MEMORY_PREFIX}${token}:${code}`) === '1';
  } catch {
    return false;
  }
}

/**
 * @param {string} token
 * @param {string} code
 */
function markBottleCodeHandled(token, code) {
  try {
    sessionStorage.setItem(`${BOTTLE_CODE_MEMORY_PREFIX}${token}:${code}`, '1');
  } catch {
    // Geen opslag (privévenster): hoogstens één extra poging in een volgende controle
  }
}

/** @returns {string[]} Code(s) uit de cookie van een code-link (/discount/CODE) */
function cookieDiscountCodes() {
  const match = document.cookie.split('; ').find((part) => part.startsWith('discount_code='));
  if (!match) return [];
  try {
    return decodeURIComponent(match.slice('discount_code='.length))
      .split(',')
      .map((code) => code.trim().toUpperCase())
      .filter(Boolean);
  } catch {
    return [];
  }
}

/**
 * Fles-code (Thema-instellingen > Qallo fles-code): staat er een code met de tag free-bottle-code in het
 * winkelmandje die nog niet van toepassing is omdat er geen product met fles in zit, dan:
 * - een Starter Pack of Starter Combo zonder fles: één stuk omzetten naar de variant met fles;
 * - anders (bv. enkel tubs): de losse fles toevoegen.
 * Niets doen in een leeg winkelmandje (enkel cadeau, verzendbescherming of LoyaltyLion-beloning telt niet), als er
 * al een product met fles in zit (dan geldt de code om een andere reden niet) of als de code voor dit winkelmandje
 * al eens afgehandeld is. De korting zelf (€ 17 op de varianten met fles en de flessen) blijft in Shopify.
 * @param {QalloCartConfig} config
 * @param {any} cart - /cart.js
 * @param {CartLine[]} lines
 * @returns {GiftAction | null}
 */
function decideBottleAction(config, cart, lines) {
  const { codes, pairs, bottleProductId, bottleVariantId, available } = config.bottleCode;
  if (!cart?.token) return null;

  // Losse fles (toegevoegd voor een fles-code) naast een variant met fles: de code geeft één keer € 17 korting en
  // Shopify verdeelt die over beide lijnen, waardoor de losse fles betaald wordt. De losse fles weghalen.
  const pairedBottles = new Set(Object.values(pairs).map(Number));
  const looseBottle = lines.find((line) => line.bottleCodeProperty);
  if (looseBottle && lines.some((line) => pairedBottles.has(line.variantId))) {
    return { type: 'remove', key: looseBottle.key };
  }

  if (codes.length === 0) return null;

  const pending = (cart.discount_codes ?? []).find(
    (/** @type {{ code: string, applicable: boolean }} */ entry) =>
      entry.applicable === false && codes.includes(String(entry.code).trim().toUpperCase())
  );
  if (!pending) return null;
  const code = String(pending.code).trim().toUpperCase();
  const token = String(cart.token);
  if (bottleCodeHandled(token, code)) return null;

  const products = lines.filter(
    (line) => !isGiftLine(config, line) && !isProtectionLine(config, line) && !line.lionReward
  );
  if (products.length === 0) return null;

  const bottleVariants = new Set(Object.values(pairs).map(Number));
  const hasBottle = products.some(
    (line) => bottleVariants.has(line.variantId) || (bottleProductId > 0 && line.productId === bottleProductId)
  );
  if (hasBottle) return null;

  const plain = products.find((line) => pairs[String(line.variantId)]);
  if (plain) {
    return {
      type: 'swapBottle',
      key: plain.key,
      quantity: plain.quantity,
      variantId: Number(pairs[String(plain.variantId)]),
      code,
      token,
    };
  }

  if (bottleVariantId && available) return { type: 'addBottle', variantId: bottleVariantId, code, token };
  return null;
}

/** Wachttijd na het laatste signaal (cart-event, extern request, focus) voor een controle start. */
const RECONCILE_DEBOUNCE_MS = 200;
/** Afrekenen: wachttijd voor een aantalwijziging die nog in de debounce van Horizon zit (300 ms) plus marge. */
const CHECKOUT_QUANTITY_GRACE_MS = 400;
/** Afrekenen: hoogstens zo lang wachten op lopende wijzigingen en de laatste controle, daarna toch door. */
const CHECKOUT_SETTLE_TIMEOUT_MS = 8000;
/** Hoogstens zo lang wachten op lopende cart-events van Horizon en andere bronnen (vangnet tegen een hangende promise). */
const PENDING_EVENT_TIMEOUT_MS = 10000;
/** Nieuwe controle na een tijdelijke fout (429, netwerk), daarna telkens de laatste waarde. */
const RECONCILE_RETRY_DELAYS_MS = [3000, 6000, 12000, 24000, 30000];
/** Vangnet tegen lussen: hoogstens zoveel cadeauwijzigingen per minuut, daarna pauze tot het venster vrij is. */
const MAX_GIFT_CHANGES_PER_MINUTE = 8;
/** Vangnet tegen lussen: hoogstens zoveel opeenvolgende runs zonder rust (dirty tijdens elke run). */
const MAX_CONSECUTIVE_RUNS = 6;

/**
 * Brengt het cadeau (en de verzendbescherming) in lijn met het winkelmandje: toevoegen of verwijderen volgens
 * decideGiftAction. Eén geserialiseerde controle ("reconcile") die altijd vertrekt van het verse winkelmandje
 * (/cart.js of het antwoord van de eigen wijziging), nooit van de items in een cart-event: die kunnen ouder zijn
 * dan een wijziging die nog loopt (bv. twee keer toevoegen via het productformulier, waar Horizon het lopende
 * /cart.json-request hergebruikt).
 *
 * - Elk signaal (cart-event van Horizon of een app, extern request via CartRequestWatcher, focus, zichtbaar worden
 *   van de pagina, bfcache) vraagt een controle aan. Loopt er al een, dan wordt die als dirty gemarkeerd en volgt
 *   er na afloop nog één: een aanvraag gaat nooit verloren.
 * - Een controle wacht eerst tot lopende cart-events en aantalwijzigingen van Horizon afgerond zijn.
 * - Tijdelijke fout (429, netwerk, ook na de pogingen van fetchCart/postCart): nieuwe controle na 3, 6, 12, 24 en
 *   daarna telkens 30 s, zolang de pagina zichtbaar is. Zichtbaar worden of focus start meteen een nieuwe controle.
 * - Een stap die Shopify weigert (bv. uitverkocht) wordt voor dezelfde situatie niet herhaald.
 * - Vangnetten tegen lussen: hoogstens MAX_GIFT_ROUNDS stappen per run, MAX_CONSECUTIVE_RUNS runs na elkaar,
 *   MAX_GIFT_CHANGES_PER_MINUTE cadeauwijzigingen per minuut; daarna een pauze en één nieuwe controle.
 */
class GiftManager {
  #consumers = 0;
  #started = false;
  #initialCheckDone = false;
  /** De pagina (Liquid) is nog niet vergeleken met het verse winkelmandje */
  #pageSyncPending = true;
  #running = false;
  #dirty = false;
  /** Stappen die Shopify weigerde (bv. uitverkocht), per situatie; die worden niet herhaald. */
  /** @type {Set<string>} */
  #rejectedActions = new Set();
  /** Lopende cart-events (hun promise): een controle wacht tot ze afgerond zijn. */
  /** @type {Set<Promise<unknown>>} */
  #pendingEvents = new Set();
  /** @type {number[]} Tijdstippen van eigen cadeauwijzigingen (vangnet tegen lussen) */
  #changeTimes = [];
  #retryIndex = 0;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #retryTimer;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #pauseTimer;
  #request = debounce(() => this.#run(), RECONCILE_DEBOUNCE_MS);

  /** Luistert voor de hele levensduur van de pagina; zonder config (geen balk, geen verzendbescherming) doet een controle niets. */
  start() {
    if (this.#started) return;
    this.#started = true;
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartEvent);
    document.addEventListener(StandardEvents.cartDiscountUpdate, this.#onCartEvent);
    document.addEventListener(ThemeEvents.cartSectionRestored, this.#onSignal);
    window.addEventListener('pageshow', this.#onPageShow);
    window.addEventListener('focus', this.#onSignal);
    window.addEventListener('online', this.#onSignal);
    document.addEventListener('visibilitychange', this.#onVisibility);
  }

  connect() {
    this.#consumers += 1;
    this.start();

    // Eerste controle met de lijnen die Liquid meegaf: geen request als alles klopt
    if (!this.#initialCheckDone) {
      this.#initialCheckDone = true;
      const config = readConfig();
      if (config && decideGiftAction(config, config.lines)) this.request();
      // Code-link met een fles-code en al iets in het winkelmandje: één controle (die leest /cart.js)
      else if (
        config &&
        config.lines.length > 0 &&
        cookieDiscountCodes().some((code) => config.bottleCode.codes.includes(code))
      ) {
        this.request();
      }
    }
  }

  disconnect() {
    this.#consumers = Math.max(0, this.#consumers - 1);
  }

  /**
   * Voor afrekenen: wacht tot lopende aantalwijzigingen klaar zijn en doet één verse controle (cadeau, fles-code),
   * zodat de checkout nooit een cadeau bevat dat er niet meer hoort (dat zou betaald worden). Hoogstens timeoutMs.
   * @param {number} timeoutMs
   */
  async settleForCheckout(timeoutMs) {
    const work = (async () => {
      // Een aantalveld stuurt zijn wijziging pas na de debounce van Horizon (300 ms): die eerst laten starten
      await wait(CHECKOUT_QUANTITY_GRACE_MS);
      await this.#settle();
      while (this.#running) await wait(50);
      await this.#run();
    })();
    await Promise.race([work.catch(() => {}), wait(timeoutMs)]);
  }

  /** Vraagt een controle aan (gebundeld); loopt er een, dan volgt er na afloop nog één. */
  request() {
    if (this.#running) {
      this.#dirty = true;
      return;
    }
    this.#request();
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onCartEvent = (event) => {
    if (ownGiftEvents.has(event)) return;

    const promise = event.promise;
    if (!promise) {
      this.request();
      return;
    }

    // Ook na een fout of een geweigerde promise: het winkelmandje kan toch gewijzigd zijn (bv. toegevoegd, maar
    // /cart.json gaf daarna 429). De controle leest het zelf opnieuw.
    const settled = promise.then(
      () => {},
      () => {}
    );
    this.#pendingEvents.add(settled);
    if (this.#running) this.#dirty = true;
    settled.finally(() => {
      this.#pendingEvents.delete(settled);
      this.request();
    });
  };

  #onSignal = () => {
    this.#retryIndex = 0;
    this.request();
  };

  /** @param {PageTransitionEvent} event */
  #onPageShow = (event) => {
    if (event.persisted) this.#onSignal();
  };

  #onVisibility = () => {
    if (document.visibilityState === 'visible') this.#onSignal();
  };

  /** Wacht op lopende cart-events en aantalwijzigingen van Horizon (met een maximum). */
  async #settle() {
    const deadline = performance.now() + PENDING_EVENT_TIMEOUT_MS;
    while (performance.now() < deadline) {
      const pending = [...this.#pendingEvents, ...CartItemsComponent.pendingQuantityUpdates];
      if (pending.length === 0) return;
      const remaining = deadline - performance.now();
      await Promise.race([Promise.allSettled(pending), wait(Math.max(0, remaining))]);
    }
  }

  /** @returns {boolean} true als er nog een cadeauwijziging mag binnen het venster van een minuut */
  #mayChange() {
    const now = Date.now();
    this.#changeTimes = this.#changeTimes.filter((time) => now - time < 60000);
    return this.#changeTimes.length < MAX_GIFT_CHANGES_PER_MINUTE;
  }

  /** Pauze (vangnet tegen lussen) en daarna één nieuwe controle. */
  #pause() {
    const oldest = this.#changeTimes[0] ?? Date.now();
    const delay = Math.max(5000, 60000 - (Date.now() - oldest));
    console.warn(`[qallo-cart] Gift paused for ${Math.round(delay / 1000)} s (too many changes)`);
    clearTimeout(this.#pauseTimer);
    this.#pauseTimer = setTimeout(() => this.request(), delay);
  }

  /** Tijdelijke fout: nieuwe controle met een oplopende pauze (enkel zolang de pagina zichtbaar is). */
  #retryLater() {
    const delay = RECONCILE_RETRY_DELAYS_MS[Math.min(this.#retryIndex, RECONCILE_RETRY_DELAYS_MS.length - 1)];
    this.#retryIndex += 1;
    clearTimeout(this.#retryTimer);
    this.#retryTimer = setTimeout(() => {
      // Verborgen tab: wachten op visibilitychange (geen requests op de achtergrond)
      if (document.visibilityState === 'hidden') return;
      this.request();
    }, delay);
  }

  async #run() {
    if (this.#running) {
      this.#dirty = true;
      return;
    }
    this.#running = true;
    clearTimeout(this.#retryTimer);

    let failedTemporarily = false;
    let paused = false;
    try {
      for (let run = 0; run < MAX_CONSECUTIVE_RUNS; run++) {
        this.#dirty = false;
        const outcome = await this.#reconcile();
        if (outcome === 'error') {
          failedTemporarily = true;
          break;
        }
        if (outcome === 'paused') {
          paused = true;
          break;
        }
        // Tijdens de run een nieuw signaal: nog een keer, met een vers winkelmandje
        if (!this.#dirty) break;
        if (run === MAX_CONSECUTIVE_RUNS - 1) {
          // Vangnet: niet blijven lopen, wel later nog eens kijken
          failedTemporarily = true;
        }
      }
    } finally {
      this.#running = false;
    }

    if (paused) return;
    if (failedTemporarily) {
      this.#retryLater();
      return;
    }
    this.#retryIndex = 0;
    // Signaal net na de laatste controle (tussen de check op dirty en het einde)
    if (this.#dirty) {
      this.#dirty = false;
      this.request();
    }
  }

  /**
   * Eén controle: vers winkelmandje lezen en stap voor stap bijsturen.
   * @returns {Promise<'done' | 'error' | 'paused'>}
   */
  async #reconcile() {
    try {
      await this.#settle();
      const config = readConfig();
      if (!config) return 'done';

      /** @type {any} */
      let cart = await fetchCart();
      /** @type {CartLine[]} */
      let lines = normalizeLines(cart.items);

      // Eerste controle na het laden: toont de pagina andere lijnen dan het winkelmandje (bv. een cadeau dat op de
      // achtergrond werd toegevoegd terwijl de pagina laadde), dan de pagina één keer verversen
      if (this.#pageSyncPending) {
        this.#pageSyncPending = false;
        const shown = new Map(config.lines.map((line) => [line.key, line.quantity]));
        const differs =
          shown.size !== lines.length || lines.some((line) => shown.get(line.key) !== line.quantity);
        if (differs) cartRequestWatcher.syncPage(config.lines);
      }

      for (let round = 0; round < MAX_GIFT_ROUNDS; round++) {
        // Eerst het cadeau en de verzendbescherming, daarna de fles-code
        const action = decideGiftAction(config, lines) ?? decideBottleAction(config, cart, lines);
        if (!action) return 'done';

        const signature = `${JSON.stringify(action)}@${eligibleValue(config, lines)}`;
        if (this.#rejectedActions.has(signature)) return 'done';
        if (!this.#mayChange()) {
          this.#pause();
          return 'paused';
        }
        this.#changeTimes.push(Date.now());

        try {
          cart = await this.#apply(action);
          lines = normalizeLines(cart.items);
        } catch (error) {
          console.warn('[qallo-cart] Gift update failed', error);
          if (error instanceof CartRequestError && error.isRejection) {
            this.#rejectedActions.add(signature);
            cart = await fetchCart();
            lines = normalizeLines(cart.items);
            continue;
          }
          return 'error';
        }

        // Ondertussen een andere wijziging: die eerst laten afronden en opnieuw lezen
        if (this.#dirty) return 'done';
      }
      return 'done';
    } catch (error) {
      console.warn('[qallo-cart] Gift check failed', error);
      return 'error';
    }
  }

  /**
   * Voert één stap uit en meldt die met een CartLinesUpdateEvent, zodat de drawer opnieuw rendert.
   * @param {GiftAction} action
   * @returns {Promise<any>} Het verse winkelmandje
   */
  async #apply(action) {
    /** @type {any} */
    let data;
    /** @type {'update' | 'remove'} */
    let eventAction;
    /** @type {{ id: string, quantity: number }[]} */
    let eventLines;

    if (action.type === 'swapBottle') {
      // Eén request (atomair): één stuk zonder fles minder en de variant met fles erbij. Zo kan een onderbreking
      // (bv. de klant navigeert weg) nooit een extra product met fles achterlaten zonder dat het andere wegging.
      // Geen property op deze lijn: /cart/update.js voegt per variant toe, zonder properties. Daarom een
      // winkelmandje-attribuut (komt mee in de bestelling) om omgezette varianten te kunnen tellen.
      // /cart/update.js zet absolute aantallen: die vlak ervoor vers lezen. Anders kan een toevoeging die intussen
      // binnenkwam (bv. twee keer snel op toevoegen) verloren gaan.
      const fresh = await fetchCart();
      const line = (fresh.items ?? []).find((/** @type {any} */ item) => item.key === action.key);
      const bottleQuantity = (fresh.items ?? [])
        .filter((/** @type {any} */ item) => Number(item.variant_id) === action.variantId)
        .reduce((/** @type {number} */ sum, /** @type {any} */ item) => sum + Number(item.quantity || 0), 0);
      if (!line || bottleQuantity > 0) {
        // Lijn intussen weg of al een variant met fles: niets omzetten
        markBottleCodeHandled(action.token, action.code);
        return fresh;
      }
      const remaining = Math.max(0, Number(line.quantity) - 1);
      try {
        data = await postCart(`${Theme.routes.cart_update_url}.js`, {
          updates: { [action.key]: remaining, [String(action.variantId)]: 1 },
          attributes: { [BOTTLE_SWAP_ATTRIBUTE]: action.code },
        });
      } finally {
        // Eén keer per winkelmandje en code (ook bij een weigering): haalt de klant de fles weg, dan blijft ze weg
        markBottleCodeHandled(action.token, action.code);
      }
      eventAction = 'update';
      eventLines = [{ id: action.key, quantity: remaining }];
    } else if (action.type === 'addBottle') {
      try {
        data = await postCart(Theme.routes.cart_add_url, {
          items: [{ id: action.variantId, quantity: 1, properties: { [BOTTLE_CODE_PROPERTY]: action.code } }],
        });
      } finally {
        markBottleCodeHandled(action.token, action.code);
      }
      const key = data.items?.[0]?.key ?? String(action.variantId);
      // 'update': de drawer opent niet vanzelf, maar rendert opnieuw als hij open is
      eventAction = 'update';
      eventLines = [{ id: key, quantity: 1 }];
    } else if (action.type === 'add') {
      data = await postCart(Theme.routes.cart_add_url, {
        items: [{ id: action.variantId, quantity: 1, properties: { [GIFT_PROPERTY]: '1' } }],
      });
      const key = data.items?.[0]?.key ?? String(action.variantId);
      // 'update' i.p.v. 'add': een automatisch cadeau opent de drawer niet. Let op: het request naar /cart/add.js
      // telt voor Shopify-analytics en Converge wel als "Added To Cart" (die volgen het netwerkrequest, niet dit event).
      eventAction = 'update';
      eventLines = [{ id: key, quantity: 1 }];
    } else {
      const quantity = action.type === 'update' ? action.quantity : 0;
      data = await postCart(Theme.routes.cart_change_url, { id: action.key, quantity });
      eventAction = quantity > 0 ? 'update' : 'remove';
      eventLines = [{ id: action.key, quantity }];
    }

    // /cart/change geeft het volledige winkelmandje terug, /cart/add.js enkel de toegevoegde lijn
    /** @type {any} */
    let cart = data;
    if (!isFullCart(data)) {
      try {
        cart = await fetchCart();
      } catch (error) {
        // De wijziging zelf is gelukt, enkel /cart.js bleef falen (bv. 429): drawer en teller later verversen.
        // De volgende controle (retryLater) leest het winkelmandje opnieuw, dus geen tweede cadeau.
        cartRequestWatcher.refreshLater(action.type === 'add');
        throw error;
      }
    }

    // Liep er ondertussen een andere wijziging, dan is de section-HTML van dit antwoord mogelijk ouder dan wat de
    // drawer al toont: zonder sections rendert cart-items-component zichzelf opnieuw (met de stale-guard van Horizon)
    const sections = this.#dirty || this.#pendingEvents.size > 0 ? undefined : data.sections;
    const target = document.querySelector('qallo-cart-rewards-component') ?? document;
    dispatchCartUpdate(
      target,
      { action: eventAction, lines: eventLines },
      cart,
      sections,
      'qallo-cart-gift',
      (event) => ownGiftEvents.add(event)
    );

    return cart;
  }
}

const giftManager = new GiftManager();
giftManager.start();

/**
 * Afrekenen vanuit drawer of winkelmandjepagina. Horizon verstuurt daarvoor het winkelmandje-formulier met alle
 * aantallen (updates[...]). Dat geeft twee problemen: een cadeau dat net nog weg moest, gaat mee en wordt betaald,
 * en een verouderde pagina (tweede tab, terugknop, lijn op de achtergrond gewijzigd) stuurt aantallen voor lijnen die
 * niet meer bestaan (foutpagina) of voor de verkeerde lijn. Alle aantalwijzigingen gaan al direct naar Shopify, dus:
 * eerst lopende wijzigingen en één controle afwachten, dan naar de checkout zonder aantallen mee te sturen.
 */
let checkoutPending = false;

/** @param {SubmitEvent} event */
function onCartFormSubmit(event) {
  const form = event.target;
  if (!(form instanceof HTMLFormElement) || form.id !== 'cart-form') return;
  event.preventDefault();
  if (checkoutPending) return;
  checkoutPending = true;

  const submitter = event.submitter instanceof HTMLButtonElement ? event.submitter : null;
  submitter?.setAttribute('aria-busy', 'true');
  if (submitter) submitter.disabled = true;

  // Een aantal dat de klant typte en nog niet verliet: eerst laten versturen
  const active = document.activeElement;
  if (active instanceof HTMLInputElement && form.contains(active)) active.blur();

  const note = form.querySelector('textarea[name="note"]');
  giftManager.settleForCheckout(CHECKOUT_SETTLE_TIMEOUT_MS).finally(() => {
    const checkout = document.createElement('form');
    checkout.method = 'post';
    checkout.action = Theme.routes.cart_url;
    checkout.hidden = true;
    const fields = { checkout: '' };
    if (note instanceof HTMLTextAreaElement) fields.note = note.value;
    for (const [name, value] of Object.entries(fields)) {
      const input = document.createElement('input');
      input.type = 'hidden';
      input.name = name;
      input.value = value;
      checkout.append(input);
    }
    document.body.append(checkout);
    checkout.submit();
  });
}

document.addEventListener('submit', onCartFormSubmit, true);
// Terug uit de checkout (bfcache): knop weer bruikbaar
window.addEventListener('pageshow', (event) => {
  if (!event.persisted) return;
  checkoutPending = false;
  for (const button of document.querySelectorAll('button[name="checkout"][aria-busy="true"]')) {
    if (!(button instanceof HTMLButtonElement)) continue;
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }
});

/** Pad van een cart-request dat het winkelmandje wijzigt (met of zonder .js, ook met taalprefix zoals /nl-be). */
const CART_MUTATION_PATH = /\/cart\/(add|change|update|clear)(\.js)?$/;
/** Wachttijd na het laatste externe request (een app doet soms meerdere requests na elkaar). */
const EXTERNAL_DEBOUNCE_MS = 300;
/** Vangnet tegen lussen: hoogstens zoveel verversingen per minuut. */
const EXTERNAL_MAX_REFRESHES_PER_MINUTE = 12;

/**
 * Bewaakt wijzigingen aan het winkelmandje door andere scripts (bv. de Mix & Match-builder van Easy Bundle, die zelf
 * /cart/add.js aanroept, of een losse /cart/change.js). Die sturen geen theme-event, waardoor de drawer, de teller en
 * de cadeaulogica pas bij herladen bijwerken. Upcart deed dit door fetch en XMLHttpRequest te overschrijven; hier
 * zonder monkey patching: een PerformanceObserver ziet elk afgerond request (fetch en XHR) als 'resource'-entry.
 *
 * Eigen requests worden genegeerd: postCart en elk cart-event van het theme (dat Horizon vóór zijn request verstuurt)
 * claimen een tijdvenster; een request dat binnen een claim start, is van het theme. Na een extern request volgt
 * één verversing via de weg van Horizon (CartLinesUpdateEvent met section-HTML): drawer, teller, cadeaulogica en
 * upsells werken bij, en na een toevoeging opent de drawer (instelling "Winkelwagen automatisch openen").
 * Een extern request zonder gewijzigde lijnen (bv. cart-attributen) kost één /cart.js en verder niets.
 */
class CartRequestWatcher {
  /** @type {PerformanceObserver | null} */
  #observer = null;
  /** @type {{ from: number, until: number }[]} */
  #claims = [];
  /** @type {{ start: number, add: boolean }[]} */
  #pending = [];
  /** @type {number[]} */
  #refreshTimes = [];
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;
  #busy = false;
  /** @type {boolean | null} Verversing aangevraagd tijdens een lopende verversing (true = toevoeging) */
  #queued = null;
  /** Laatst gekende aantallen per lijn, voor de lijnen in het event en om niets te doen als er niets veranderde. */
  /** @type {Map<string, { variantId: number, quantity: number }> | null} */
  #known = null;
  /** Eerste klik of toets van de bezoeker gezien (zie #onEntries). */
  #interacted = false;

  start() {
    if (this.#observer) return;
    if (typeof PerformanceObserver === 'undefined' || !PerformanceObserver.supportedEntryTypes?.includes('resource')) {
      return;
    }

    const config = readConfig();
    if (config) this.#remember(config.lines.map((line) => ({ ...line, variant_id: line.variantId })));

    for (const name of [StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate, StandardEvents.cartNoteUpdate]) {
      // Capture: de claim staat er voordat de dispatcher (Horizon) zijn request start
      document.addEventListener(name, this.#onThemeEvent, { capture: true });
    }

    const onInteraction = () => {
      this.#interacted = true;
      window.removeEventListener('pointerdown', onInteraction, true);
      window.removeEventListener('keydown', onInteraction, true);
    };
    window.addEventListener('pointerdown', onInteraction, { capture: true, passive: true });
    window.addEventListener('keydown', onInteraction, { capture: true, passive: true });

    this.#observer = new PerformanceObserver((list) => this.#onEntries(list.getEntries()));
    this.#observer.observe({ type: 'resource' });
  }

  /**
   * Claimt de cart-requests die vanaf nu starten tot de teruggegeven functie wordt aangeroepen.
   * @returns {() => void}
   */
  claim() {
    const claim = { from: performance.now(), until: Infinity };
    this.#claims.push(claim);
    let released = false;
    const release = () => {
      if (released) return;
      released = true;
      claim.until = performance.now();
    };
    // Vangnet: een claim zonder afloop vervalt
    setTimeout(release, 20000);
    return release;
  }

  /** @param {Event & { promise?: Promise<any> }} event */
  #onThemeEvent = (event) => {
    const release = this.claim();
    if (!event.promise) {
      setTimeout(release, 5000);
      return;
    }
    event.promise
      .then((result) => {
        const items = result?.detail?.items;
        if (Array.isArray(items) && items.every((item) => item && 'key' in item && 'quantity' in item)) {
          this.#remember(items);
        }
      })
      .catch(() => {})
      .finally(release);
  };

  /**
   * De pagina toont deze lijnen (uit Liquid): verversen als het verse winkelmandje ervan afwijkt.
   * @param {CartLine[]} pageLines
   */
  syncPage(pageLines) {
    this.#known = new Map(
      pageLines.map((line) => [String(line.key), { variantId: line.variantId, quantity: line.quantity }])
    );
    this.#refresh(false);
  }

  /** @param {any[]} items - items uit /cart.js of een cart-event */
  #remember(items) {
    this.#known = new Map(
      items.map((item) => [String(item.key), { variantId: Number(item.variant_id), quantity: Number(item.quantity) }])
    );
  }

  /** @param {PerformanceEntryList} entries */
  #onEntries(entries) {
    let found = false;
    for (const entry of entries) {
      const initiator = /** @type {PerformanceResourceTiming} */ (entry).initiatorType;
      if (initiator !== 'fetch' && initiator !== 'xmlhttprequest') continue;

      let url;
      try {
        url = new URL(entry.name, window.location.origin);
      } catch {
        continue;
      }
      const match = url.origin === window.location.origin ? CART_MUTATION_PATH.exec(url.pathname) : null;
      if (!match) continue;
      // Apps zetten bij het laden cart-attributen via /cart/update.js (geen lijnen): pas na de eerste klik of
      // toets meetellen, zodat het laden van een pagina geen extra requests van deze module kost
      if (match[1] === 'update' && !this.#interacted) continue;

      this.#pending.push({ start: entry.startTime, add: match[1] === 'add' });
      found = true;
    }
    if (!found) return;

    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#evaluate(), EXTERNAL_DEBOUNCE_MS);
  }

  /**
   * Verversing van drawer en teller na een eigen wijziging die gelukt is, maar waarvan het verse winkelmandje
   * niet opgehaald kon worden (bv. /cart.js gaf 429). Wacht eerst even, zodat Shopify weer requests aanneemt.
   * @param {boolean} added - true na een toevoeging
   * @param {number} [delayMs]
   */
  refreshLater(added, delayMs = 8000) {
    setTimeout(() => {
      this.#refresh(added);
      giftManager.request();
    }, delayMs);
  }

  /** @param {number} start */
  #isClaimed(start) {
    return this.#claims.some((claim) => start >= claim.from - 50 && start <= claim.until + 50);
  }

  #evaluate() {
    const external = this.#pending.filter((request) => !this.#isClaimed(request.start));
    this.#pending = [];

    // Afgelopen claims ouder dan een minuut zijn niet meer nodig
    const now = performance.now();
    this.#claims = this.#claims.filter((claim) => claim.until === Infinity || now - claim.until < 60000);

    if (external.length === 0) return;
    // Cadeau rechtstreeks laten controleren: ook als de verversing hieronder wordt overgeslagen (vangnet per minuut)
    giftManager.request();
    this.#refresh(external.some((request) => request.add));
  }

  /** @param {boolean} added */
  async #refresh(added) {
    if (this.#busy) {
      this.#queued = Boolean(this.#queued) || added;
      return;
    }

    const now = Date.now();
    this.#refreshTimes = this.#refreshTimes.filter((time) => now - time < 60000);
    if (this.#refreshTimes.length >= EXTERNAL_MAX_REFRESHES_PER_MINUTE) {
      console.warn('[qallo-cart] Too many external cart changes, cart refresh skipped');
      return;
    }
    this.#refreshTimes.push(now);

    this.#busy = true;
    try {
      const cart = await fetchCart();
      const previous = this.#known;
      const change = this.#describeChange(previous, cart.items, added);
      // Niets veranderd (bv. enkel cart-attributen bijgewerkt): geen verversing nodig
      if (!change) return;

      const sections = await this.#fetchSections();
      const target = document.querySelector('qallo-cart-rewards-component, qallo-cart-protection-component');
      dispatchCartUpdate(target ?? document, change, cart, sections, 'qallo-cart-external');
    } catch (error) {
      console.warn('[qallo-cart] Cart refresh after external change failed', error);
    } finally {
      this.#busy = false;
    }

    if (this.#queued !== null) {
      const queued = this.#queued;
      this.#queued = null;
      this.#refresh(queued);
    }
  }

  /**
   * Lijnen voor het CartLinesUpdateEvent, uit het verschil met de laatst gekende lijnen.
   * @param {Map<string, { variantId: number, quantity: number }> | null} previous
   * @param {any[]} items
   * @param {boolean} added
   * @returns {{ action: 'add', lines: { merchandiseId: string, quantity: number }[] } | { action: 'update', lines: { id: string, quantity: number }[] } | null}
   */
  #describeChange(previous, items, added) {
    /** @type {{ merchandiseId: string, quantity: number }[]} */
    const addedLines = [];
    /** @type {{ id: string, quantity: number }[]} */
    const changedLines = [];
    const seen = new Set();

    for (const item of items) {
      const key = String(item.key);
      seen.add(key);
      const before = previous?.get(key)?.quantity ?? 0;
      const quantity = Number(item.quantity);
      if (quantity === before) continue;
      changedLines.push({ id: key, quantity });
      if (quantity > before) addedLines.push({ merchandiseId: String(item.variant_id), quantity: quantity - before });
    }
    for (const key of previous?.keys() ?? []) {
      if (!seen.has(key)) changedLines.push({ id: key, quantity: 0 });
    }

    // Zonder gekende vorige stand: altijd verversen
    if (previous && changedLines.length === 0) return null;
    if (added && addedLines.length > 0) return { action: 'add', lines: addedLines };
    return { action: 'update', lines: changedLines };
  }

  /**
   * Verse section-HTML van alle cart-items-componenten (drawer, winkelwagenpagina), in één request.
   * @returns {Promise<Record<string, string> | undefined>}
   */
  async #fetchSections() {
    const ids = getCartSectionIds();
    if (ids.length === 0) return undefined;

    try {
      // Zelfde URL als de section-renderer van Horizon (huidige pagina met zijn parameters)
      const url = new URL(window.location.href);
      url.hash = '';
      url.searchParams.set('sections', ids.join(','));
      const response = await fetch(url, { cache: 'no-store' });
      if (!response.ok) return undefined;
      return await response.json();
    } catch {
      // Zonder section-HTML ververst cart-items-component zichzelf
      return undefined;
    }
  }
}

const cartRequestWatcher = new CartRequestWatcher();
cartRequestWatcher.start();

/**
 * Beloningsbalk. De balk zelf is volledig server-side (Liquid, morph na elke wijziging);
 * dit element start enkel de cadeaulogica.
 */
class QalloCartRewardsComponent extends Component {
  connectedCallback() {
    super.connectedCallback();
    giftManager.connect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    giftManager.disconnect();
  }
}

/**
 * Schakelaar verzendbescherming: voegt de variant toe of verwijdert ze.
 *
 * @typedef {object} ProtectionRefs
 * @property {HTMLInputElement} input
 * @property {HTMLElement} [error]
 *
 * @extends {Component<ProtectionRefs>}
 */
class QalloCartProtectionComponent extends Component {
  requiredRefs = ['input'];
  #busy = false;

  connectedCallback() {
    super.connectedCallback();
    // Ook zonder beloningsbalk: verzendbescherming weg als er geen ander product meer is
    giftManager.connect();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    giftManager.disconnect();
  }

  /** @param {Event} event */
  async toggle(event) {
    const { input, error } = this.refs;
    if (!(event.target instanceof HTMLInputElement)) return;

    // Tijdens een request niet opnieuw schakelen (niet `disabled`: dat zou de focus wegnemen)
    if (this.#busy) {
      input.checked = !input.checked;
      return;
    }

    const turnOn = input.checked;
    const variantId = this.dataset.variantId ?? '';
    const lineKey = this.dataset.lineKey ?? '';
    if (!variantId) return;

    this.#busy = true;
    this.setAttribute('aria-busy', 'true');
    input.setAttribute('aria-disabled', 'true');
    if (error) {
      error.hidden = true;
      error.textContent = '';
    }

    try {
      /** @type {any} */
      let data;
      if (turnOn) {
        // Property _qallo_protection: zo herkennen theme en function (Qallo Cart Gift) de lijn
        data = await postCart(Theme.routes.cart_add_url, {
          items: [{ id: Number(variantId), quantity: 1, properties: { [PROTECTION_PROPERTY]: '1' } }],
        });
      } else if (lineKey) {
        data = await postCart(Theme.routes.cart_change_url, { id: lineKey, quantity: 0 });
      } else {
        data = await postCart(Theme.routes.cart_update_url, { updates: { [variantId]: 0 } });
      }

      /** @type {any} */
      let cart = data;
      if (!isFullCart(data)) {
        try {
          cart = await fetchCart();
        } catch (refreshError) {
          // De wijziging zelf is gelukt (bv. /cart.js gaf 429): geen foutmelding, later verversen
          console.warn('[qallo-cart] Cart refresh after shipping protection update failed', refreshError);
          cartRequestWatcher.refreshLater(turnOn);
          return;
        }
      }
      dispatchCartUpdate(
        this,
        turnOn
          ? { action: 'add', lines: [{ merchandiseId: variantId, quantity: 1 }] }
          : { action: 'remove', lines: [{ id: lineKey || variantId, quantity: 0 }] },
        cart,
        data.sections,
        'qallo-cart-protection'
      );
    } catch (requestError) {
      console.warn('[qallo-cart] Shipping protection update failed', requestError);
      input.checked = !turnOn;
      if (error) {
        error.textContent = userErrorMessage(requestError, this.dataset.errorMessage ?? '');
        error.hidden = false;
      }
      dispatchCartError(this, requestError, 'Shipping protection update failed');
    } finally {
      this.#busy = false;
      this.removeAttribute('aria-busy');
      input.removeAttribute('aria-disabled');
    }
  }
}

/**
 * Upsells: haalt sections/qallo-cart-upsells.liquid op via de Section Rendering API wanneer de drawer opent
 * of het winkelmandje wijzigt (nooit bij het laden van de pagina) en zet het resultaat in dit element.
 * De morph van de drawer laat de inhoud met rust (data-skip-subtree-update).
 */
class QalloCartUpsellsComponent extends Component {
  /** @type {AbortController | null} */
  #listeners = null;
  /** @type {AbortController | null} */
  #request = null;
  /** @type {AbortController | null} */
  #scrollListeners = null;
  #stale = true;
  #productId = '';
  /** Producten in het winkelmandje bij de laatste geslaagde load; zelfde set = zelfde upsells. */
  /** @type {string | null} */
  #loadedSignature = null;
  /** @type {string | null} */
  #cartSignature = null;
  #debouncedLoad = debounce(() => this.#load(), 150);

  connectedCallback() {
    super.connectedCallback();

    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;

    this.#drawer?.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen, { signal });
    document.addEventListener(StandardEvents.cartLinesUpdate, this.#onCartUpdate, { signal });

    this.#productId = this.dataset.productId ?? '';
    this.#cartSignature = cartSignature((this.dataset.cartProductIds ?? '').split(',').filter(Boolean));
    this.#stale = true;
    if (this.#isShown) this.#load();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#listeners?.abort();
    this.#listeners = null;
    this.#request?.abort();
    this.#request = null;
    this.#scrollListeners?.abort();
    this.#scrollListeners = null;
    this.#debouncedLoad.cancel();
  }

  /** @returns {(HTMLElement & { isOpen?: boolean }) | null} */
  get #drawer() {
    return this.closest('theme-drawer');
  }

  /** Buiten een drawer altijd zichtbaar; in de drawer enkel als die open is. Verborgen bij te veel productlijnen. */
  get #isShown() {
    if (this.hidden) return false;
    const drawer = this.#drawer;
    if (!drawer) return true;
    return drawer.isOpen ?? drawer.hasAttribute('open');
  }

  get #excludedProductIds() {
    return (this.dataset.excludedProductIds ?? '').split(',').filter(Boolean);
  }

  #onDrawerOpen = () => {
    // Verborgen (te veel productlijnen): niets ophalen
    if (this.#stale && !this.hidden) this.#load();
  };

  /** @param {CartLinesUpdateEvent} event */
  #onCartUpdate = (event) => {
    // Het automatische cadeau verandert niets aan de upsells (cadeauproducten worden nooit getoond)
    if (ownGiftEvents.has(event)) return;

    event.promise
      ?.then(({ detail }) => {
        if (detail?.didError) return;

        const items = /** @type {any[] | undefined} */ (detail?.items);
        if (Array.isArray(items)) {
          const excluded = this.#excludedProductIds;
          const productLines = items.filter(
            (item) =>
              !item.properties?.[GIFT_PROPERTY] &&
              !item.properties?.[LEGACY_GIFT_PROPERTY] &&
              !item.properties?.[PROTECTION_PROPERTY] &&
              !excluded.includes(String(item.product_id))
          );
          // Zelfde regel als snippets/qallo-cart-upsells.liquid: een gekochte fles (cadeauproduct zonder property)
          // telt als productlijn, maar is nooit de basis voor aanbevelingen
          const giftProductIds = (this.dataset.giftProductIds ?? '').split(',').filter(Boolean);
          const base = productLines.find((item) => !giftProductIds.includes(String(item.product_id)));
          this.#productId = base ? String(base.product_id) : '';
          this.#cartSignature = cartSignature(productLines.map((item) => String(item.product_id)));
          // Zelfde regel als snippets/qallo-cart-upsells.liquid: boven het maximum aantal productlijnen geen upsells.
          // Meteen toepassen, de morph van de drawer zet hetzelfde attribuut kort daarna.
          const maxLines = Number(this.dataset.maxLines) || 0;
          if (maxLines > 0) {
            const hide = productLines.length > maxLines;
            const hadFocus = this.contains(document.activeElement);
            this.hidden = hide;
            if (hide && hadFocus) this.#moveFocusOut();
          }
        } else {
          this.#cartSignature = null;
        }

        // Enkel een ander aantal van dezelfde producten: upsells blijven gelijk
        if (this.#cartSignature !== null && this.#cartSignature === this.#loadedSignature) return;

        this.#stale = true;
        if (this.#isShown) this.#debouncedLoad();
      })
      .catch(() => {});
  };

  /** @returns {URL} */
  #buildUrl() {
    if (this.dataset.mode === 'automatic' && this.#productId && this.dataset.recommendationsUrl) {
      const url = new URL(this.dataset.recommendationsUrl, window.location.origin);
      url.searchParams.set('product_id', this.#productId);
      url.searchParams.set('limit', String(RECOMMENDATIONS_LIMIT));
      url.searchParams.set('intent', this.dataset.intent || 'related');
      url.searchParams.set('section_id', UPSELLS_SECTION_ID);
      return url;
    }

    const url = new URL(this.dataset.cartUrl || Theme.routes.cart_url, window.location.origin);
    url.searchParams.set('section_id', UPSELLS_SECTION_ID);
    return url;
  }

  async #load() {
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    this.#stale = false;
    const signature = this.#cartSignature;

    try {
      const response = await fetch(this.#buildUrl(), { signal: request.signal, cache: 'no-store' });
      if (!response.ok) throw new Error(`Upsells fetch failed: ${response.status}`);
      const html = await response.text();
      if (request.signal.aborted || !this.isConnected) return;

      const inner = new DOMParser().parseFromString(html, 'text/html').querySelector('.qallo-upsells__inner');
      const hasProducts = inner instanceof HTMLElement && Number(inner.dataset.count) > 0;

      // Focus bewaren als die in de upsells stond (bv. op de knop van een net toegevoegd product)
      const active = document.activeElement;
      const focusedIndex =
        active && this.contains(active)
          ? [...this.querySelectorAll('.qallo-upsells__item')].findIndex((item) => item.contains(active))
          : -1;

      this.replaceChildren(...(hasProducts ? [document.importNode(inner, true)] : []));
      this.#loadedSignature = signature;
      this.#setupScroll();

      if (focusedIndex !== -1) this.#restoreFocus(focusedIndex);
    } catch (error) {
      if (error?.name === 'AbortError') return;
      this.#stale = true;
      console.warn('[qallo-cart] Upsells could not be loaded', error);
    } finally {
      if (this.#request === request) this.#request = null;
    }
  }

  /** @param {number} index */
  #restoreFocus(index) {
    // Enkel het zichtbare product is bedienbaar (de andere zijn inert, zie #updateNavigation)
    const buttons = [...this.querySelectorAll('.qallo-upsells__add')].filter(
      (button) => !button.closest('.qallo-upsells__item')?.inert
    );
    const target = buttons[Math.min(index, buttons.length - 1)];
    if (target instanceof HTMLElement) {
      target.focus({ preventScroll: true });
      return;
    }

    // Geen upsells meer: focus naar het dialoogvenster van de drawer
    const dialog = this.closest('dialog');
    if (dialog instanceof HTMLElement) dialog.focus({ preventScroll: true });
  }

  get #list() {
    const list = this.querySelector('.qallo-upsells__list');
    return list instanceof HTMLElement ? list : null;
  }

  #setupScroll() {
    this.#scrollListeners?.abort();
    this.#scrollListeners = null;
    const list = this.#list;
    if (!list) return;

    this.#scrollListeners = new AbortController();
    list.addEventListener('scroll', this.#updateNavigation, { passive: true, signal: this.#scrollListeners.signal });
    this.#updateNavigation();
  }

  #updateNavigation = () => {
    const list = this.#list;
    if (!list) return;

    const scrolled = Math.abs(list.scrollLeft);
    const maxScroll = list.scrollWidth - list.clientWidth;
    const previous = this.querySelector('.qallo-upsells__nav-button--previous');
    const next = this.querySelector('.qallo-upsells__nav-button--next');
    if (previous instanceof HTMLButtonElement) previous.disabled = scrolled <= 1;
    if (next instanceof HTMLButtonElement) next.disabled = scrolled >= maxScroll - 1;

    // Eén product per keer, dus de index volgt uit de scrollpositie
    const items = list.querySelectorAll('.qallo-upsells__item');
    const first = items[0];
    if (!(first instanceof HTMLElement) || !first.offsetWidth) return;
    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const current = Math.min(items.length, Math.round(scrolled / (first.offsetWidth + gap)) + 1);

    // Enkel het zichtbare product is bedienbaar: de andere zijn inert (niet in de tabvolgorde en niet voorgelezen).
    // Swipen en de pijlen werken gewoon; na elke scroll volgt het zichtbare product. Focus in een product dat
    // inert wordt (zeldzaam, bv. scrollen met de focus op een knop), gaat naar het zichtbare product.
    const hadFocus = this.contains(document.activeElement);
    items.forEach((item, index) => {
      if (!(item instanceof HTMLElement)) return;
      const hide = index !== current - 1;
      if (item.inert !== hide) item.inert = hide;
    });
    if (hadFocus && (!document.activeElement || document.activeElement === document.body)) {
      const visibleButton = items[current - 1]?.querySelector('.qallo-upsells__add');
      if (visibleButton instanceof HTMLElement) visibleButton.focus({ preventScroll: true });
    }

    // Teller "2 van 5"
    const counter = this.querySelector('.qallo-upsells__counter');
    if (counter instanceof HTMLElement && counter.dataset.template) {
      const text = counter.dataset.template
        .replace('[current]', String(current))
        .replace('[total]', String(items.length));
      if (counter.textContent !== text) counter.textContent = text;
    }
  };

  /** @param {1 | -1} direction */
  #scrollBy(direction) {
    const list = this.#list;
    const item = list?.querySelector('.qallo-upsells__item');
    if (!list || !(item instanceof HTMLElement)) return;

    const gap = parseFloat(getComputedStyle(list).columnGap) || 0;
    const rtl = getComputedStyle(list).direction === 'rtl' ? -1 : 1;
    list.scrollBy({
      left: direction * rtl * (item.offsetWidth + gap),
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  }

  scrollPrevious() {
    this.#scrollBy(-1);
  }

  scrollNext() {
    this.#scrollBy(1);
  }

  /** @param {Event} event */
  selectVariant(event) {
    const select = event.target;
    if (!(select instanceof HTMLSelectElement)) return;

    const option = select.selectedOptions[0];
    const card = select.closest('.qallo-upsells__card');
    if (!option || !card) return;

    const price = card.querySelector('[data-qallo-price] bdi');
    const comparePrice = card.querySelector('[data-qallo-compare-price]');
    const button = card.querySelector('.qallo-upsells__add');

    if (price) price.textContent = option.dataset.price ?? '';
    if (comparePrice instanceof HTMLElement) {
      const compareBdi = comparePrice.querySelector('bdi');
      if (compareBdi) compareBdi.textContent = option.dataset.comparePrice ?? '';
      comparePrice.hidden = !option.dataset.comparePrice;
    }
    if (button instanceof HTMLElement) {
      button.dataset.variantId = option.value;
      if (option.dataset.priceAmount) button.dataset.priceAmount = option.dataset.priceAmount;
    }
  }

  /**
   * Scrollt de lijn van een net toegevoegde upsell in beeld, zodra de drawer of winkelwagenpagina die toont
   * (de morph van cart-items-component volgt kort na het event).
   * @param {string} key
   */
  async #revealLine(key) {
    const container = this.closest('cart-items-component');
    if (!container) return;

    const selector = `.cart-items__table-row[data-key="${CSS.escape(key)}"]`;
    const deadline = performance.now() + 2000;
    while (performance.now() < deadline) {
      await new Promise((resolve) => requestAnimationFrame(resolve));
      if (!this.isConnected) return;
      const row = container.querySelector(selector);
      if (row instanceof HTMLElement && row.getClientRects().length > 0 && !row.classList.contains('adding')) {
        row.scrollIntoView({ block: 'nearest', behavior: prefersReducedMotion() ? 'auto' : 'smooth' });
        return;
      }
    }
  }

  /** @param {Event} event */
  async addToCart(event) {
    const button = event.target;
    if (!(button instanceof HTMLButtonElement)) return;
    if (button.getAttribute('aria-busy') === 'true') return;

    const variantId = button.dataset.variantId;
    if (!variantId) return;

    const card = button.closest('.qallo-upsells__card');
    const error = card?.querySelector('.qallo-upsells__error');
    if (error instanceof HTMLElement) {
      error.hidden = true;
      error.textContent = '';
    }

    button.setAttribute('aria-busy', 'true');

    try {
      /** @type {any} */
      let data;
      try {
        // Het antwoord bevat meteen de verse section-HTML van drawer en winkelwagenpagina (sections, sections_url).
        // Met de upsell-property maakt Shopify een aparte lijn, ook als dezelfde variant al zonder property in het
        // winkelmandje zit: zo is in de bestelling te zien welke stuks via de upsell kwamen.
        data = await postCart(Theme.routes.cart_add_url, {
          items: [{ id: Number(variantId), quantity: 1, properties: { [UPSELL_PROPERTY]: '1' } }],
        });
      } catch (requestError) {
        // Enkel hier een foutmelding: het toevoegen zelf is mislukt
        console.warn('[qallo-cart] Upsell add failed', requestError);
        if (error instanceof HTMLElement) {
          error.textContent = userErrorMessage(requestError, this.dataset.errorMessage ?? '');
          error.hidden = false;
        }
        dispatchCartError(this, requestError, 'Add to cart failed');
        return;
      }

      // /cart/add.js gaf 200: het product zit in het winkelmandje. Meting nu al, los van wat hierna volgt.
      this.#trackUpsellAdded(button);

      try {
        const cart = await fetchCart();
        dispatchCartUpdate(
          this,
          { action: 'add', lines: [{ merchandiseId: variantId, quantity: 1 }] },
          cart,
          data.sections,
          'qallo-cart-upsells'
        );
        const key = data.items?.[0]?.key;
        if (key) this.#revealLine(String(key));
      } catch (refreshError) {
        // /cart.js bleef falen (bv. 429) na een geslaagde toevoeging: geen foutmelding (anders voegt de klant het
        // product nog eens toe), drawer en teller later verversen
        console.warn('[qallo-cart] Cart refresh after upsell add failed, retrying later', refreshError);
        cartRequestWatcher.refreshLater(true);
      }
    } finally {
      button.removeAttribute('aria-busy');
    }

    // De upsells verdwijnen bij te veel productlijnen (ook via de morph van de drawer): focus niet verliezen
    requestAnimationFrame(() => {
      const active = document.activeElement;
      if (this.hidden && (!active || active === document.body || this.contains(active))) this.#moveFocusOut();
    });
  }

  /**
   * Meting na een geslaagde upsell: custom event "Cart Upsell Added" naar Converge (enkel als window.cvg er al is;
   * Converge regelt de toestemming zelf, hier wordt niets geladen) en een DOM-event 'qallo:upsell-added' (bubbelt)
   * met dezelfde gegevens. Een fout in de meting mag het toevoegen nooit hinderen.
   * @param {HTMLButtonElement} button - Toevoegknop van de upsell (data-attributen uit sections/qallo-cart-upsells.liquid)
   */
  #trackUpsellAdded(button) {
    const priceCents = Number(button.dataset.priceAmount);
    const properties = {
      product_id: Number(button.dataset.productId) || button.dataset.productId || '',
      variant_id: Number(button.dataset.variantId) || button.dataset.variantId || '',
      product_title: button.dataset.productTitle ?? '',
      price: Number.isFinite(priceCents) ? priceCents / 100 : null,
      mode: this.dataset.mode === 'fixed' ? 'fixed' : 'automatic',
      position: Number(button.dataset.position) || null,
    };

    try {
      const cvg = /** @type {any} */ (window).cvg;
      if (typeof cvg === 'function') cvg({ method: 'track', eventName: 'Cart Upsell Added', properties });
    } catch (error) {
      console.warn('[qallo-cart] Converge tracking failed', error);
    }

    this.dispatchEvent(new CustomEvent('qallo:upsell-added', { bubbles: true, detail: { ...properties } }));
  }

  /** Focus naar het volgende bedienbare element na de upsells in de drawer (bv. Kortingscode toevoegen). */
  #moveFocusOut() {
    const scope = this.closest('dialog') ?? this.closest('cart-items-component');
    if (!scope) return;
    const candidates = scope.querySelectorAll('summary, button:not([disabled]), input:not([type="hidden"]), a[href], select');
    for (const element of candidates) {
      if (!(element instanceof HTMLElement) || this.contains(element) || !element.getClientRects().length) continue;
      if (this.compareDocumentPosition(element) & Node.DOCUMENT_POSITION_FOLLOWING) {
        element.focus({ preventScroll: true });
        return;
      }
    }
  }
}

/**
 * Kortingscode in de sticky footer: de knop "Kortingscode toevoegen" maakt op dezelfde regel plaats voor het veld
 * en Toepassen (attribuut data-expanded). Escape of het veld leeg verlaten zet de knop terug. Toepassen zelf doet
 * cart-discount-component (Horizon); de morph daarna rendert de knop opnieuw (de server kent data-expanded niet).
 * Bewust een gewoon HTMLElement en geen Component: on:submit in het formulier moet naar cart-discount-component.
 */
class QalloDiscountToggle extends HTMLElement {
  /** @type {AbortController | null} */
  #listeners = null;
  /** @type {MutationObserver | null} */
  #observer = null;
  #ownChange = false;

  connectedCallback() {
    this.#listeners?.abort();
    this.#listeners = new AbortController();
    const { signal } = this.#listeners;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    this.addEventListener('focusout', this.#onFocusOut, { signal });

    // Na een morph (bv. code toegepast) is data-expanded weg: focus niet verliezen
    this.#observer?.disconnect();
    this.#observer = new MutationObserver(() => {
      // Eigen wijziging (knop, Escape, veld verlaten): niets te doen
      if (this.#ownChange) {
        this.#ownChange = false;
        return;
      }
      if (this.hasAttribute('data-expanded')) return;
      const active = document.activeElement;
      if (!active || active === document.body || (this.contains(active) && !this.#toggle?.contains(active))) {
        this.#toggle?.focus({ preventScroll: true });
      }
    });
    this.#observer.observe(this, { attributes: true, attributeFilter: ['data-expanded'] });
  }

  disconnectedCallback() {
    this.#listeners?.abort();
    this.#listeners = null;
    this.#observer?.disconnect();
    this.#observer = null;
  }

  get #toggle() {
    const toggle = this.querySelector('[data-qallo-discount-toggle]');
    return toggle instanceof HTMLButtonElement ? toggle : null;
  }

  get #input() {
    const input = this.querySelector('input[name="discount"]');
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @param {boolean} expanded */
  #setExpanded(expanded) {
    if (this.hasAttribute('data-expanded') === expanded) return;
    this.#ownChange = true;
    this.toggleAttribute('data-expanded', expanded);
    this.#toggle?.setAttribute('aria-expanded', String(expanded));
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const toggle = this.#toggle;
    if (!toggle || !(event.target instanceof Node) || !toggle.contains(event.target)) return;
    this.#setExpanded(true);
    // Zonder scroll: het veld staat in de sticky footer en is al in beeld; iOS mag de pagina niet verschuiven
    this.#input?.focus({ preventScroll: true });
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (event.key !== 'Escape' || !this.hasAttribute('data-expanded')) return;
    // Niet ook de drawer sluiten
    event.preventDefault();
    event.stopPropagation();
    this.#setExpanded(false);
    this.#toggle?.focus();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!this.hasAttribute('data-expanded')) return;
    if (event.relatedTarget instanceof Node && this.contains(event.relatedTarget)) return;
    if (this.#input?.value.trim()) return;
    this.#setExpanded(false);
  };
}

if (!customElements.get('qallo-discount-toggle')) {
  customElements.define('qallo-discount-toggle', QalloDiscountToggle);
}

/**
 * Drawer volgt het zichtbare deel van het scherm (iOS-toetsenbord).
 *
 * Op iOS Safari verandert 100dvh niet als het toetsenbord opent: enkel de visual viewport krimpt en Safari
 * verschuift die soms (offsetTop > 0) om het gefocuste veld te tonen. De vaste drawer blijft dan op de
 * layout viewport staan en de sticky footer met het kortingsveld zit onder het toetsenbord. Zolang het
 * toetsenbord open is (visual viewport duidelijk kleiner dan de layout viewport), zet deze helper
 * `--qallo-vv-height`, `--qallo-vv-top` en `data-qallo-keyboard` op <theme-drawer id="cart-drawer">.
 * snippets/cart-drawer.liquid laat de dialog zelf op 100dvh staan (achtergrond dekt het hele scherm) en
 * geeft enkel de binnenkant (header, lijst, footer) die hoogte en verschuiving, zodat de footer net boven het
 * toetsenbord staat en de pagina eronder nooit bloot komt.
 *
 * Het toetsenbord schuift op iOS 250 tot 400 ms in en visualViewport geeft tijdens die animatie tussenwaarden.
 * Daarom meet de helper na elke focus, resize en scroll een rAF-lus lang (circa 700 ms) opnieuw en past hij de
 * variabelen aan zodra ze veranderen, met een laatste meting op het einde.
 *
 * Terwijl het kortingsveld focus heeft (modal breedte, aanraakscherm) staat body bovendien op position: fixed.
 * Horizon zet enkel overflow: hidden op html en dat negeert iOS bij het in beeld brengen van een veld.
 * De vergrendeling gaat weg bij blur of bij het sluiten van de dialog, met herstel van de scrollpositie. Ze
 * loopt enkel tijdens de focus, zodat Horizon bij het sluiten de juiste scrollpositie leest.
 *
 * Browsers zonder window.visualViewport: niets te doen, de drawer blijft 100dvh.
 */
class QalloCartDrawerViewport {
  /** @type {HTMLElement | null} */
  #drawer = null;
  /** @type {AbortController | null} */
  #viewportListeners = null;
  /** @type {AbortController | null} */
  #focusListeners = null;
  /** @type {{ scrollY: number, position: string, top: string, left: string, width: string } | null} */
  #bodyLock = null;
  #modalQuery = window.matchMedia('(max-width: 989px)');
  #coarseQuery = window.matchMedia('(pointer: coarse)');
  /** @type {number | null} */
  #settleFrame = null;
  #settleUntil = 0;
  /** @type {{ height: number, top: number } | null} */
  #applied = null;

  /** Duur van de nameting na focus, resize of scroll (iOS-toetsenbordanimatie is 250 tot 400 ms) */
  static SETTLE_MS = 700;
  /** Vanaf dit verschil tussen layout en visual viewport geldt het toetsenbord als open */
  static KEYBOARD_MIN_PX = 150;

  constructor() {
    if (!window.visualViewport) return;
    document.addEventListener(DrawerOpenEvent.eventName, this.#onDrawerOpen);
    document.addEventListener(DrawerCloseEvent.eventName, this.#onDrawerClose);
    // Herstelpad: de drawer stond al open voor deze module laadde (geen open-event)
    const drawer = document.getElementById('cart-drawer');
    if (drawer instanceof HTMLElement && drawer.hasAttribute('open')) this.#start(drawer);
  }

  /** @param {Event} event */
  #onDrawerOpen = (event) => {
    if (event.target instanceof HTMLElement && event.target.id === 'cart-drawer') this.#start(event.target);
  };

  /** @param {Event} event */
  #onDrawerClose = (event) => {
    if (event.target instanceof HTMLElement && event.target.id === 'cart-drawer') this.#stop();
  };

  /** @param {HTMLElement} drawer */
  #start(drawer) {
    if (this.#drawer === drawer && this.#viewportListeners) return;
    this.#stop();
    this.#drawer = drawer;
    const viewport = window.visualViewport;
    if (!viewport) return;

    this.#viewportListeners = new AbortController();
    const { signal } = this.#viewportListeners;
    viewport.addEventListener('resize', this.#onViewportResize, { signal });
    viewport.addEventListener('scroll', this.#onViewportChange, { signal });
    drawer.addEventListener('focusin', this.#onFocusIn, { signal });
    drawer.addEventListener('focusout', this.#onFocusOut, { signal });
    this.#update();
  }

  #stop() {
    this.#viewportListeners?.abort();
    this.#viewportListeners = null;
    this.#stopSettling();
    this.#unlockBody();
    if (this.#drawer) this.#clear(this.#drawer);
    this.#drawer = null;
  }

  #onViewportChange = () => {
    this.#update();
    this.#settle();
  };

  #onViewportResize = () => {
    this.#update();
    this.#settle();
    // Na het krimpen (toetsenbord open) het gefocuste veld binnen de drawer in beeld houden, niet via window
    const input = this.#focusedDiscountInput();
    if (input) requestAnimationFrame(() => input.scrollIntoView({ block: 'nearest' }));
  };

  /**
   * Nameting: een rAF-lus lang de visual viewport opnieuw lezen, want tijdens de toetsenbordanimatie komen
   * resize-events met tussenwaarden (of maar één, te vroeg). Een nieuwe aanroep verlengt een lopende lus.
   */
  #settle() {
    this.#settleUntil = performance.now() + QalloCartDrawerViewport.SETTLE_MS;
    if (this.#settleFrame !== null) return;
    const tick = () => {
      this.#settleFrame = null;
      if (!this.#drawer) return;
      this.#update();
      if (performance.now() < this.#settleUntil) {
        this.#settleFrame = requestAnimationFrame(tick);
      } else {
        // Laatste meting na de lus, als de viewport pas op het einde zijn eindwaarde kreeg
        this.#update();
      }
    };
    this.#settleFrame = requestAnimationFrame(tick);
  }

  #stopSettling() {
    if (this.#settleFrame !== null) cancelAnimationFrame(this.#settleFrame);
    this.#settleFrame = null;
    this.#settleUntil = 0;
  }

  /** @param {HTMLElement} drawer */
  #clear(drawer) {
    drawer.style.removeProperty('--qallo-vv-height');
    drawer.style.removeProperty('--qallo-vv-top');
    drawer.removeAttribute('data-qallo-keyboard');
    this.#applied = null;
  }

  #update() {
    const drawer = this.#drawer;
    const viewport = window.visualViewport;
    if (!drawer || !viewport) return;

    const height = Math.round(viewport.height);
    const top = Math.round(viewport.offsetTop);
    // Toetsenbord open: visual viewport duidelijk kleiner dan de layout viewport (op iOS blijft innerHeight gelijk).
    // Enkel in modal modus (onder 990px); als zijbalk blijft Horizon's 100dvh gelden.
    const keyboard =
      this.#modalQuery.matches && window.innerHeight - viewport.height > QalloCartDrawerViewport.KEYBOARD_MIN_PX;

    if (!keyboard) {
      if (this.#applied) this.#clear(drawer);
      return;
    }

    const applied = this.#applied;
    if (applied && applied.height === height && applied.top === top) return;
    drawer.style.setProperty('--qallo-vv-height', `${height}px`);
    drawer.style.setProperty('--qallo-vv-top', `${top}px`);
    drawer.setAttribute('data-qallo-keyboard', '');
    this.#applied = { height, top };
  }

  /** @returns {HTMLInputElement | null} */
  #focusedDiscountInput() {
    const active = document.activeElement;
    if (!(active instanceof HTMLInputElement) || active.name !== 'discount') return null;
    if (!this.#drawer?.contains(active)) return null;
    return active;
  }

  /** @param {FocusEvent} event */
  #onFocusIn = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'discount') return;
    if (!this.#modalQuery.matches || !this.#coarseQuery.matches) return;
    this.#lockBody();
    // Het toetsenbord komt nu op; blijven meten tot de animatie voorbij is
    this.#settle();
  };

  /** @param {FocusEvent} event */
  #onFocusOut = (event) => {
    if (!(event.target instanceof HTMLInputElement) || event.target.name !== 'discount') return;
    this.#unlockBody();
    // Het toetsenbord sluit; blijven meten tot de binnenkant weer de volle hoogte heeft
    this.#settle();
  };

  #lockBody() {
    if (this.#bodyLock) return;
    const { style } = document.body;
    this.#bodyLock = {
      scrollY: window.scrollY,
      position: style.position,
      top: style.top,
      left: style.left,
      width: style.width,
    };
    style.position = 'fixed';
    style.top = `-${this.#bodyLock.scrollY}px`;
    style.left = '0';
    style.width = '100%';

    // Vangnet als het veld verdwijnt zonder blur (morph): loslaten zodra de dialog sluit. Het native close-event
    // komt na het scrollherstel van Horizon, dus de eigen scrollpositie wint.
    const dialog = this.#drawer?.querySelector('dialog');
    this.#focusListeners?.abort();
    this.#focusListeners = new AbortController();
    dialog?.addEventListener('close', () => this.#unlockBody(), { once: true, signal: this.#focusListeners.signal });
  }

  #unlockBody() {
    this.#focusListeners?.abort();
    this.#focusListeners = null;
    const lock = this.#bodyLock;
    if (!lock) return;
    this.#bodyLock = null;
    const { style } = document.body;
    style.position = lock.position;
    style.top = lock.top;
    style.left = lock.left;
    style.width = lock.width;
    window.scrollTo({ top: lock.scrollY, left: 0, behavior: 'instant' });
  }
}

new QalloCartDrawerViewport();

/**
 * Scrollhint in de drawer: zolang er onder de zichtbare producten nog iets staat (andere producten, upsells,
 * verzendbescherming), krijgt de lijst het attribuut data-qallo-more. De CSS (cart-drawer.liquid) laat de
 * lijst dan onderaan zichtbaar uitvloeien en geeft de sticky footer een schaduw naar boven.
 * Werkt bij scrollen, bij een andere grootte en na elke wijziging van de inhoud (morph van de drawer).
 */
const QalloCartScrollHint = (() => {
  const SELECTOR = '.cart-drawer__items';
  /** @param {Element} el */
  const check = (el) => {
    if (!(el instanceof HTMLElement)) return;
    const more = el.scrollHeight - el.clientHeight - el.scrollTop > 4;
    el.toggleAttribute('data-qallo-more', more);
  };
  const resizeObserver = new ResizeObserver((entries) => entries.forEach((entry) => check(entry.target)));
  /** @type {WeakSet<Element>} */
  const observed = new WeakSet();
  const scan = () => {
    document.querySelectorAll(SELECTOR).forEach((el) => {
      if (!observed.has(el)) {
        observed.add(el);
        resizeObserver.observe(el);
      }
      check(el);
    });
  };
  document.addEventListener(
    'scroll',
    (event) => {
      if (event.target instanceof Element && event.target.matches(SELECTOR)) check(event.target);
    },
    { capture: true, passive: true }
  );
  let queued = false;
  const queueScan = () => {
    if (queued) return;
    queued = true;
    requestAnimationFrame(() => {
      queued = false;
      scan();
    });
  };
  const start = () => {
    const root = document.querySelector('cart-drawer-component') || document.body;
    new MutationObserver(queueScan).observe(root, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ['data-qallo-more', 'open'],
    });
    scan();
  };
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
  return { scan };
})();

if (!customElements.get('qallo-cart-rewards-component')) {
  customElements.define('qallo-cart-rewards-component', QalloCartRewardsComponent);
}

if (!customElements.get('qallo-cart-protection-component')) {
  customElements.define('qallo-cart-protection-component', QalloCartProtectionComponent);
}

if (!customElements.get('qallo-cart-upsells-component')) {
  customElements.define('qallo-cart-upsells-component', QalloCartUpsellsComponent);
}

/*
 * Gedeelde helpers voor andere Qallo-modules (assets/qallo-mix-match.js, via "@theme/qallo-cart" in
 * snippets/scripts.liquid). Zelfde module-instantie als de <script>-tags van het winkelmandje (zelfde URL), dus
 * ook dezelfde bewaking van externe requests: requests via postCart tellen als eigen requests van het theme.
 */
export {
  CartRequestError,
  cartRequestWatcher,
  dispatchCartError,
  QalloCartScrollHint,
  dispatchCartUpdate,
  fetchCart,
  postCart,
  userErrorMessage,
};
